import React, { createContext, useContext, useState, useEffect } from 'react';

const ProjectContext = createContext(null);

const STORAGE_KEY = 'vibe-coach-projects';

export const ProjectProvider = ({ children }) => {
    const [projects, setProjects] = useState([]);
    const [currentProject, setCurrentProject] = useState(null);

    // Load projects from localStorage on mount
    useEffect(() => {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored) {
            try {
                const parsed = JSON.parse(stored);
                setProjects(parsed);
            } catch (e) {
                console.error('Failed to parse stored projects:', e);
            }
        }
    }, []);

    // Save projects to localStorage when they change
    useEffect(() => {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(projects));
    }, [projects]);

    const saveProject = (idea, blueprint) => {
        const newProject = {
            id: Date.now().toString(),
            idea,
            blueprint,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
        };
        setProjects(prev => [newProject, ...prev]);
        setCurrentProject(newProject);
        return newProject;
    };

    const updateProject = (id, updates) => {
        setProjects(prev => prev.map(p => 
            p.id === id 
                ? { ...p, ...updates, updatedAt: new Date().toISOString() }
                : p
        ));
        if (currentProject?.id === id) {
            setCurrentProject(prev => ({ ...prev, ...updates, updatedAt: new Date().toISOString() }));
        }
    };

    const deleteProject = (id) => {
        setProjects(prev => prev.filter(p => p.id !== id));
        if (currentProject?.id === id) {
            setCurrentProject(null);
        }
    };

    const loadProject = (id) => {
        const project = projects.find(p => p.id === id);
        if (project) {
            setCurrentProject(project);
        }
        return project;
    };

    const clearCurrentProject = () => {
        setCurrentProject(null);
    };

    return (
        <ProjectContext.Provider value={{
            projects,
            currentProject,
            saveProject,
            updateProject,
            deleteProject,
            loadProject,
            clearCurrentProject,
        }}>
            {children}
        </ProjectContext.Provider>
    );
};

export const useProjects = () => {
    const context = useContext(ProjectContext);
    if (!context) {
        throw new Error('useProjects must be used within a ProjectProvider');
    }
    return context;
};
